import { MutationPayload, Plugin, Store } from 'vuex'
import { PromiseUtils } from '@/shared/util/promise-utils'
import moduleResource, { Module } from '@/api/resources/ModuleResource'
import { LaboratoryConfigurationResponse } from '@/api/resources/common/initialization/common-initialization.dto'
import { LaboratoryIncidentsStoreState, RootStoreState } from '@/store/root/root-store.model'
import { commonSimpleListService } from '@/api/services/common/simple-list/common-simple-list.service'
import { SimpleListEntityEnum, SimpleListParams } from '@/api/services/common/simple-list/common-simple-list.dto'
import sessionStore from '@/session/sessionStore'

async function _getModules(
    laboratoriesConfiguration: LaboratoryConfigurationResponse[],
    laboratoryId: number
): Promise<Module[]> {
    const modules =
        await PromiseUtils.toCallSecure<Module[]>(
            moduleResource.getModulesFromLaboratoryConfiguration(
                laboratoriesConfiguration,
                laboratoryId))
    return modules ?? []
}
async function _getModulesFromFirstLaboratoryWithModules(
    store: Store<RootStoreState>,
    laboratoriesConfiguration: LaboratoryConfigurationResponse[],
    laboratoryId: number
): Promise<Module[]> {
    const modules = await _getModules(laboratoriesConfiguration, laboratoryId)
    if (!modules.length) {
        const labsConfiguration =
            laboratoriesConfiguration.filter(labConfiguration => labConfiguration.laboratory.labId !== laboratoryId)
        if (labsConfiguration.length) {
            return _getModulesFromFirstLaboratoryWithModules(
                store,
                labsConfiguration,
                labsConfiguration[0].laboratory.labId)
        }
    } else if (laboratoryId !== store.getters.labId) {
        sessionStore.updateLabId(laboratoryId)
    }
    return modules
}
async function loadModules(store: Store<RootStoreState>, laboratoryId: number): Promise<void> {
    const modules =
        await _getModulesFromFirstLaboratoryWithModules(
            store,
            store.getters.laboratoriesConfiguration,
            laboratoryId)
    store.commit('updateModules', modules)
}

async function loadLaboratoryIncidents(store: Store<RootStoreState>, laboratoryId: number) {
    const params: SimpleListParams = {
        laboratoryId
    }
    const incidentOrigins = await commonSimpleListService.getSimpleList(SimpleListEntityEnum.INCIDENT_ORIGIN, params)
    const incidentTypes = await commonSimpleListService.getSimpleList(SimpleListEntityEnum.INCIDENT_TYPE, params)

    const laboratoryIncidents: LaboratoryIncidentsStoreState = {
        laboratoryId,
        incidents: {
            origins: incidentOrigins.content,
            types: incidentTypes.content
        }
    }
    store.commit('updateLaboratoryIncidents', laboratoryIncidents)
}

export const laboratoryChangedPlugin: Plugin<RootStoreState> =
    (store: Store<RootStoreState>) => {
        store.subscribe(async (mutation: MutationPayload) => {
            if (mutation.type === 'updateLabId') {
                const laboratoryId: number = mutation.payload

                await loadModules(store, laboratoryId)
                if (laboratoryId === store.getters.labId) {
                    await sessionStore.getLaboratoryParams(laboratoryId)
                }

                await loadLaboratoryIncidents(store, laboratoryId)
            }
        })
    }
